// Pure data layer of the guided first run of Canchas: initial data, validations and the requests.
// Create uses the same builders as the club form (buildClubFormData for the club, buildCourtsPayload
// for the courts); every optional step updates sending only its own fields. The club update endpoint
// is partial (absent fields keep their value), the courts bulk upsert is NOT: it replaces by name, so
// every courts save carries ALL courts with ALL their pricing (see `modelosDeCanchas`).
import { buildClubFormData, emptyClubForm } from "../ClubForm";
import { buildCourtsPayload } from "../CourtLayoutTab";
import { SPORT_LABELS } from "../../../constants/courtDomain";
import {
	DIAS_SEMANA,
	MAX_GALERIA,
	coordValida,
	faltaContacto,
	faltaUbicacion,
	num,
	telefonoCompleto,
	turnosDe,
} from "../../reservas/primerUso/barPayload";
import { COUNTRIES } from "../../../pages/reservas/components/ReservaShared";
import { recordatoriosDeVenue } from "../../recordatorios/recordatorios";

export {
	MAX_GALERIA,
	coordValida,
	faltaContacto,
	faltaUbicacion,
	num,
	turnosDe,
	telefonoCompleto,
	DIAS_SEMANA,
};

export const DURACIONES = [60, 90, 120];

// Where each optional step lives in the club form (SECTION_IDS of ClubForm).
export const SECCION_DEL_FORMULARIO = {
	contacto: "info",
	fotos: "info",
	servicios: "deportes",
	politicas: "politicas",
	detalle: "canchas",
	reglas: "canchas",
};

export const nuevaCancha = (nombre = "") => ({
	nombre,
	deporte: "",
	superficie: "",
	precios: {}, // minutes -> price
	techado: "",
	categoria: "",
	luz: false,
	requiereSena: false,
	sena: "",
});

/** Starting values; the name comes from the portal negocio and stays editable. */
export const datosInicialesClub = (negocio = "") => ({
	nombre: negocio,
	nombreDelPortal: negocio,
	onlineBookingEnabled: false,
	stateid: "",
	cityid: "",
	address: "",
	lat: "",
	lng: "",
	deportes: [],
	dias: [1, 2, 3, 4, 5, 6, 0],
	desde: "10:00",
	hasta: "23:00",
	duraciones: [60, 90],
	canchas: [nuevaCancha("Cancha 1")],
	// optional steps
	descripcion: "",
	prefijo: COUNTRIES[0].prefix,
	telefono: "",
	email: "",
	web: "",
	instagram: "",
	portada: null,
	galeria: [],
	servicios: [],
	categorias: "",
	ventana: 6,
	antelacion: 120,
	tolerancia: 15,
	cancela: true,
	plazo: 24,
	notas: "",
	reglas: [],
});

/** Selected durations, ascending. */
export const duracionesDe = (d) =>
	[...d.duraciones].map(Number).sort((a, b) => a - b);

/** The court's sport: the chosen one if it is still among the club sports, or the only sport. */
export const deporteDe = (c, d) =>
	d.deportes.includes(c.deporte)
		? c.deporte
		: d.deportes.length === 1
			? d.deportes[0]
			: "";

export const categoriasDe = (texto) =>
	String(texto || "")
		.split(",")
		.map((x) => x.trim())
		.filter(Boolean);

/* ---- validations (return the first missing thing, "" when fine) ---- */

export const faltaClub = (d) =>
	!d.nombre.trim() ? "Falta el nombre del club" : faltaUbicacion(d);

export const faltaHorarios = (d) =>
	!d.deportes.length
		? "Elegí al menos un deporte"
		: !d.dias.length
			? "Elegí al menos un día"
			: !d.duraciones.length
				? "Elegí al menos una duración"
				: !turnosDe(d.desde, d.hasta, duracionesDe(d)[0]).length
					? "El horario no alcanza para un turno"
					: "";

export const faltaCanchas = (d) => {
	if (!d.canchas.length) return "Agregá al menos una cancha";
	const sin = d.canchas.find((c) => !c.nombre.trim());
	if (sin) return "Hay una cancha sin nombre";
	const nombres = d.canchas.map((c) => c.nombre.trim().toLowerCase());
	if (new Set(nombres).size !== nombres.length)
		return "Dos canchas tienen el mismo nombre";
	for (const c of d.canchas) {
		if (!deporteDe(c, d)) return `Elegí el deporte de ${c.nombre.trim()}`;
		for (const m of duracionesDe(d)) {
			if (!(num(c.precios[m]) > 0))
				return `Falta el precio de ${c.nombre.trim()} (${m} min)`;
		}
	}
	return "";
};

export const precioMinimo = (c, d) =>
	Math.min(...duracionesDe(d).map((m) => num(c.precios[m])));

export const faltaDetalle = (d) => {
	for (const c of d.canchas) {
		if (!c.requiereSena) continue;
		if (!(num(c.sena) > 0)) return `Falta la seña de ${c.nombre}`;
		if (num(c.sena) > precioMinimo(c, d))
			return `La seña de ${c.nombre} supera su precio más bajo`;
	}
	return "";
};

export const faltaReglas = (d) => {
	for (const r of d.reglas) {
		if (!(num(r.precio) > 0)) return "Hay una regla sin precio";
		if (Boolean(r.desde) !== Boolean(r.hasta))
			return "Completá desde y hasta, o dejá los dos vacíos";
		if (r.sena !== "" && r.sena != null) {
			if (!(num(r.sena) > 0))
				return "La seña de una regla debe ser mayor que $0";
			if (num(r.sena) > num(r.precio))
				return "Una seña supera el precio de su regla";
		}
	}
	return "";
};

export const nuevaRegla = (d) => ({
	cancha: d.canchas[0]?.nombre.trim() || "",
	dur: duracionesDe(d)[0] || DURACIONES[0],
	dia: "", // "" = cualquier día, "0".."6" = weekday
	desde: "",
	hasta: "",
	precio: "",
	sena: "",
});

/* ---- club requests ---- */

export const horariosDe = (d) =>
	DIAS_SEMANA.map(({ weekday }) => ({
		weekday,
		enabled: d.dias.includes(weekday),
		open: d.desde,
		close: d.hasta,
	}));

/**
 * What a copied club (`d.importado`, see importarConfig) adds to the create request: contact, policies,
 * services, categories, amenities and reminders. Birthdays and courts go after the club exists.
 */
const camposImportadosClub = (d) => ({
	shortDescription: d.importado.shortDescription || "",
	description: d.descripcion.trim(),
	contactPhone: telefonoCompleto(d),
	contactEmail: d.email.trim(),
	website: d.web.trim(),
	instagram: d.instagram.trim(),
	reservationWindowDays: d.ventana,
	minAdvanceMinutes: d.antelacion,
	toleranceMinutes: d.tolerancia,
	cancellationAllowed: Boolean(d.cancela),
	cancellationPolicyHours: d.plazo,
	additionalNotes: d.notas.trim(),
	services: d.servicios,
	courtCategoriesText: d.categorias,
	amenitiesText: (d.importado.amenities || []).join(", "),
	gridGranularityMinutes: d.importado.gridGranularityMinutes || 30,
	...recordatoriosDeVenue(d.importado.reminders),
});

/** The form state buildClubFormData reads for the create request (same defaults as the club form). */
export const formularioCrearClub = (d) => ({
	...emptyClubForm(),
	onlineBookingEnabled: d.onlineBookingEnabled === true,
	name: d.nombre.trim(),
	address: d.address.trim(),
	lat: d.lat,
	lng: d.lng,
	cityid: d.cityid,
	stateid: d.stateid,
	sports: d.deportes,
	allowedDurations: duracionesDe(d),
	openingHours: horariosDe(d),
	...(d.importado ? camposImportadosClub(d) : {}),
});

export const formDataCrearClub = (d) =>
	buildClubFormData(formularioCrearClub(d));

const base = (clubId) => {
	const fd = new FormData();
	fd.append("clubId", clubId);
	return fd;
};

export const formDataContacto = (clubId, d) => {
	const fd = base(clubId);
	fd.append("description", d.descripcion.trim());
	fd.append("contactPhone", telefonoCompleto(d));
	fd.append("contactEmail", d.email.trim());
	fd.append("website", d.web.trim());
	fd.append("instagram", d.instagram.trim());
	return fd;
};

export const hayFotos = (d) => Boolean(d.portada) || d.galeria.length > 0;

// No existingGallery on purpose: the backend then keeps what is there and appends (max 5).
export const formDataFotos = (clubId, d) => {
	const fd = base(clubId);
	if (d.portada) fd.append("image", d.portada);
	d.galeria.slice(0, MAX_GALERIA).forEach((f) => fd.append("galleryimage", f));
	return fd;
};

export const formDataServicios = (clubId, d) => {
	const fd = base(clubId);
	fd.append("services", JSON.stringify(d.servicios));
	fd.append("courtCategories", JSON.stringify(categoriasDe(d.categorias)));
	return fd;
};

export const formDataPoliticas = (clubId, d) => {
	const fd = base(clubId);
	fd.append("reservationWindowDays", String(d.ventana));
	fd.append("minAdvanceMinutes", String(d.antelacion));
	fd.append("toleranceMinutes", String(d.tolerancia));
	fd.append("cancellationAllowed", String(Boolean(d.cancela)));
	fd.append("cancellationPolicyHours", String(d.plazo));
	fd.append("additionalNotes", d.notas.trim());
	return fd;
};

/* ---- courts (the shape CourtLayoutTab holds and buildCourtsPayload serializes) ---- */

/** Courts as created by the required steps: base prices only, no details, no rules, no deposits. */
export const canchasIniciales = (d) =>
	d.canchas.map((c) => ({
		name: c.nombre.trim(),
		sport: deporteDe(c, d),
		surface: c.superficie,
		enclosure: "",
		courtCategory: "",
		lighting: false,
		pricing: {
			rules: [],
			defaultByDuration: duracionesDe(d).map((m) => ({
				durationMinutes: m,
				price: num(c.precios[m]),
				deposit: null,
			})),
		},
	}));

const conSena = (c, n) => ({
	...c,
	pricing: {
		...c.pricing,
		defaultByDuration: c.pricing.defaultByDuration.map((r) => ({
			...r,
			deposit: n,
		})),
	},
});

/** "Detalle de cada cancha": enclosure, category, lighting and one deposit for every base price row. */
export const aplicarDetalle = (modelos, d) =>
	modelos.map((m, i) => {
		const c = d.canchas[i];
		const detalle = {
			...m,
			enclosure: c.techado,
			courtCategory: c.categoria,
			lighting: Boolean(c.luz),
		};
		return conSena(detalle, c.requiereSena ? num(c.sena) : null);
	});

/** "Precios por día y horario": replaces the pricing rules of every court. */
export const aplicarReglas = (modelos, d) =>
	modelos.map((m) => ({
		...m,
		pricing: {
			...m.pricing,
			rules: d.reglas
				.filter((r) => r.cancha === m.name)
				.map((r) => ({
					durationMinutes: Number(r.dur),
					weekday: r.dia === "" || r.dia == null ? null : Number(r.dia),
					fromTime: r.desde || "",
					toTime: r.hasta || "",
					price: num(r.precio),
					deposit: r.sena === "" || r.sena == null ? null : num(r.sena),
				})),
		},
	}));

/** Body of the bulk upsert; `sinSenas` (cobros not validated) sends every deposit as null. */
/** Courts of a copied club: base prices, details and day/time rules all at once (the steps stay editable). */
export const canchasImportadas = (d) =>
	aplicarReglas(aplicarDetalle(canchasIniciales(d), d), d);

export const payloadCanchas = (modelos, { sinSenas = false } = {}) =>
	buildCourtsPayload(modelos, { sinSenas });

export const resumenCancha = (c, d) =>
	`${c.nombre.trim() || "—"} · ${SPORT_LABELS[deporteDe(c, d)] || "—"}`;
